import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Badge } from '~/components/ui/badge';
import type { AdmissionInquiryRow } from './types';

interface InquiryDetailModalProps {
  inquiry: AdmissionInquiryRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface StudentEntry {
  firstName?: string;
  lastName?: string;
  gender?: string;
  age?: string;
  grade?: string;
  priorSchool?: string;
}

const GENDER_LABELS: Record<string, string> = { MALE: 'Male', FEMALE: 'Female' };

export function InquiryDetailModal({ inquiry, open, onOpenChange }: InquiryDetailModalProps) {
  if (!inquiry) return null;

  const metadata = (inquiry.metadataJson ?? {}) as Record<string, unknown>;
  const students = (metadata.students ?? []) as StudentEntry[];
  const date = new Date(inquiry.createdAt as string);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Inquiry from {inquiry.parentFirstName} {inquiry.parentLastName}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Submitted {date.toLocaleDateString()} at {date.toLocaleTimeString()}
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Contact Info */}
          <section>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Contact Information
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Name</span>
                <p className="font-medium">{inquiry.parentFirstName} {inquiry.parentLastName}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Email</span>
                <p className="font-medium">{inquiry.parentEmail}</p>
              </div>
              {inquiry.parentPhone && (
                <div>
                  <span className="text-muted-foreground">Phone</span>
                  <p className="font-medium">{inquiry.parentPhone}</p>
                </div>
              )}
              {!!metadata.homeChurch && (
                <div>
                  <span className="text-muted-foreground">Home Church</span>
                  <p className="font-medium">{String(metadata.homeChurch)}</p>
                </div>
              )}
            </div>
          </section>

          {/* Interest */}
          {!!(metadata.heardAbout || metadata.interest) && (
            <section>
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Interest
              </h4>
              <div className="space-y-3 text-sm">
                {!!metadata.heardAbout && (
                  <div>
                    <span className="text-muted-foreground">How did you hear about us?</span>
                    <p>{String(metadata.heardAbout)}</p>
                  </div>
                )}
                {!!metadata.interest && (
                  <div>
                    <span className="text-muted-foreground">What draws you to the school?</span>
                    <p>{String(metadata.interest)}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Students */}
          {students.length > 0 && (
            <section>
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Students ({students.length})
              </h4>
              <div className="space-y-2">
                {students.map((student, i) => {
                  const name = [student.firstName, student.lastName].filter(Boolean).join(' ');
                  const details = [
                    student.gender && GENDER_LABELS[student.gender],
                    student.age && `Age ${student.age}`,
                    student.priorSchool,
                  ].filter(Boolean);

                  return (
                    <div
                      key={i}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border p-2.5 text-sm"
                    >
                      <Badge variant="secondary">Student {i + 1}</Badge>
                      {name && <span className="font-medium">{name}</span>}
                      <span className={name ? undefined : 'font-medium'}>
                        {student.grade || 'No grade specified'}
                      </span>
                      {details.length > 0 && (
                        <span className="text-muted-foreground">({details.join(' · ')})</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Source */}
          <section className="flex items-center gap-2 text-sm text-muted-foreground pt-2 border-t">
            <span>Source:</span>
            <Badge variant="outline">{inquiry.source || 'link'}</Badge>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
